import React, { useState, useEffect, useCallback } from 'react';
import { getStats, syncData, computeAllMatchings } from '../services/api';
import ScoreBar from '../components/ScoreBar';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [matching, setMatching] = useState(false);
  const [message, setMessage] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const { data } = await getStats();
      setStats(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleSync = async () => {
    setSyncing(true);
    setMessage('');
    try {
      const { data } = await syncData();
      setMessage(`Synchronisé : ${data.jobs_synced} offres, ${data.candidates_synced} candidats`);
      loadStats();
    } catch (e) {
      setMessage('Erreur de synchronisation');
    }
    setSyncing(false);
  };

  const handleMatching = async () => {
    setMatching(true);
    setMessage('');
    try {
      const { data } = await computeAllMatchings();
      setMessage(`Matching terminé : ${data.total_matched} candidats analysés`);
      loadStats();
    } catch (e) {
      setMessage('Erreur de matching');
    }
    setMatching(false);
  };

  const pipelineLabels = {
    new: 'Nouveau', process: 'En process', hired: 'Embauché', refused: 'Refusé', offer: 'Proposition',
  };

  return (
    <div style={{ padding: 32, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1e293b' }}>Dashboard</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={handleSync} disabled={syncing} style={btnStyle('#3b82f6')}>
            {syncing ? 'Synchronisation...' : 'Sync Welcome Kit'}
          </button>
          <button onClick={handleMatching} disabled={matching} style={btnStyle('#8b5cf6')}>
            {matching ? 'Calcul en cours...' : 'Lancer le Matching'}
          </button>
        </div>
      </div>

      {message && (
        <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 24, color: '#166534' }}>
          {message}
        </div>
      )}

      {stats && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 32 }}>
            <StatCard label="Offres publiées" value={stats.jobs_count} color="#3b82f6" />
            <StatCard label="Candidats" value={stats.candidates_count} color="#8b5cf6" />
            <StatCard label="Matchings calculés" value={stats.matchings_count} color="#f59e0b" />
            <StatCard label="Score moyen" value={stats.avg_score ? `${stats.avg_score}%` : '—'} color="#22c55e" />
          </div>

          {Object.keys(stats.pipeline).length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ marginBottom: 16, color: '#1e293b' }}>Pipeline des candidats</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                {Object.entries(stats.pipeline).map(([kind, count]) => (
                  <div key={kind} style={{ textAlign: 'center', padding: 16, background: '#f8fafc', borderRadius: 8 }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>{count}</div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{pipelineLabels[kind] || kind}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.top_candidates?.length > 0 && (
            <div style={{ ...cardStyle, marginTop: 20 }}>
              <h3 style={{ marginBottom: 16, color: '#1e293b' }}>Top 10 — Meilleurs matchings</h3>
              {stats.top_candidates.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#3b82f6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                    {i + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{c.firstname} {c.lastname}</div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{c.subtitle} — {c.job_name}</div>
                  </div>
                  <div style={{ width: 160 }}>
                    <ScoreBar score={c.final_score} small />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 24,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderTop: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 32, fontWeight: 700, color: '#1e293b' }}>{value}</div>
      <div style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>{label}</div>
    </div>
  );
}

const btnStyle = (color) => ({
  background: color, color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
});

const cardStyle = {
  background: '#fff', borderRadius: 12, padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

export default Dashboard;
